import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Select, Tag, Rate, Button, Spin, Input, Typography, Avatar } from 'antd';
import {
  EnvironmentOutlined, SearchOutlined, AimOutlined,
  StarFilled, TeamOutlined, PhoneOutlined, UserOutlined
} from '@ant-design/icons';
import usePublicStore from '../../store/publicStore';
import 'leaflet/dist/leaflet.css';
import DateLine from '../../components/DateLine/DateLine';

const { Text } = Typography;
const IMG_BASE = process.env.REACT_APP_URL;
const P = '#5F33E1';

// Fix default leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons by service type
const createIcon = (color) => new L.DivIcon({
  className: 'custom-map-marker',
  html: `<div style="
    width:36px;height:36px;border-radius:50%;
    background:${color};border:3px solid #fff;
    box-shadow:0 3px 12px rgba(0,0,0,0.3);
    display:flex;align-items:center;justify-content:center;
    color:#fff;font-size:16px;
  "><svg viewBox="64 64 896 896" width="18" height="18" fill="white"><path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"/><path d="M512 140c-205.4 0-372 166.6-372 372s166.6 372 372 372 372-166.6 372-372-166.6-372-372-372zm164 588.1c-9.7 5.6-22.1 2.3-27.7-7.4-17.4-30.2-48.3-48.7-82.3-48.7s-64.9 18.5-82.3 48.7c-5.6 9.7-18 13-27.7 7.4-9.7-5.6-13-18-7.4-27.7 24.4-42.3 69.2-68.4 117.4-68.4s93 26.1 117.4 68.4c5.6 9.7 2.2 22.1-7.4 27.7zM396 512c-33.1 0-60-26.9-60-60s26.9-60 60-60 60 26.9 60 60-26.9 60-60 60zm232 0c-33.1 0-60-26.9-60-60s26.9-60 60-60 60 26.9 60 60-26.9 60-60 60z"/></svg></div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
});

const TYPE_COLORS = {
  1: '#5F33E1',   // To'yxona
  3: '#E91E63',   // Honanda
  2: '#FF9800',   // Fotograf
  4: '#4CAF50',   // Boshqa
};

const getMarkerIcon = (typeId) => createIcon(TYPE_COLORS[typeId] || '#5F33E1');

// Fly to location component
function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || 13, { duration: 1 });
  }, [center, zoom, map]);
  return null;
}

const formatPrice = (p) => p ? Number(p).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : '—';

export default function MapPage() {
  const navigate = useNavigate();
  const {
    services, loading, fetchServices, regions, serviceTypes,
    fetchRegions, fetchServiceTypes, filters, setFilter
  } = usePublicStore();

  const [search, setSearch] = useState('');
  const [flyCenter, setFlyCenter] = useState(null);
  const [flyZoom, setFlyZoom] = useState(null);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    fetchRegions();
    fetchServiceTypes();
    // Fetch more services for map
    setFilter('per_page', 100);
    fetchServices();
  }, []);

  // Services with valid coordinates
  const mapServices = useMemo(() => {
    return services.filter(s => {
      const lat = Number(s.lat || s.location?.lat);
      const lng = Number(s.long || s.lng || s.location?.lng);
      return lat && lng && !isNaN(lat) && !isNaN(lng);
    });
  }, [services]);

  // Center of Uzbekistan
  const defaultCenter = [41.311081, 69.240562];

  const handleSearch = () => {
    setFilter('search', search);
    fetchServices();
  };

  const handleRegionChange = (val) => {
    setFilter('region_id', val || null);
    const region = regions.find(r => r.id === val);
    if (region?.lat && region?.long) {
      setFlyCenter([Number(region.lat), Number(region.long)]);
      setFlyZoom(10);
    }
    fetchServices();
  };

  const handleTypeChange = (val) => {
    setFilter('service_type_id', val || null);
    fetchServices();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 70px)', background: 'var(--qk-bg-soft)' }}>
      {/* Filters bar */}
      <div style={{
        display: 'flex', gap: 10, padding: '12px 20px', background: 'var(--qk-card-bg)',
        borderBottom: '1px solid var(--qk-border)', flexWrap: 'wrap', alignItems: 'center', zIndex: 1000,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <EnvironmentOutlined style={{ color: P, fontSize: 18 }} />
          <Text strong style={{ fontSize: 16 }}>Xarita</Text>
        </div>

        <Input
          placeholder="Xizmat nomini qidiring..."
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 200, borderRadius: 10 }}
          allowClear
        />

        <Select
          placeholder="Viloyat"
          allowClear
          style={{ width: 160 }}
          value={filters.region_id}
          onChange={handleRegionChange}
          options={regions.map(r => ({ value: r.id, label: r.nameUz }))}
        />

        <Select
          placeholder="Xizmat turi"
          allowClear
          style={{ width: 160 }}
          value={filters.service_type_id}
          onChange={handleTypeChange}
          options={serviceTypes.map(t => ({ value: t.id, label: t.name }))}
        />

        <Tag style={{ borderRadius: 12, padding: '2px 10px' }}>
          {mapServices.length} ta xizmat
        </Tag>

        {loading && <Spin size="small" />}
      </div>

      {/* Map + Sidebar */}
      <div style={{ flex: 1, display: 'flex', position: 'relative' }}>
        {/* Map */}
        <div style={{ flex: 1, position: 'relative' }}>
          <MapContainer
            center={defaultCenter}
            zoom={6}
            style={{ width: '100%', height: '100%' }}
            zoomControl={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {flyCenter && <FlyTo center={flyCenter} zoom={flyZoom} />}

            {mapServices.map(svc => {
              const lat = Number(svc.lat || svc.location?.lat);
              const lng = Number(svc.long || svc.lng || svc.location?.lng);
              const icon = getMarkerIcon(svc.vendor_types_id || svc.service_type?.id);
              return (
                <Marker
                  key={svc.id}
                  position={[lat, lng]}
                  icon={icon}
                  eventHandlers={{
                    click: () => setSelectedService(svc),
                  }}
                >
                  <Popup maxWidth={280} minWidth={220}>
                    <div style={{ padding: '4px 0' }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                        {svc.images?.[0]?.file_path ? (
                          <img
                            src={IMG_BASE + svc.images[0].file_path}
                            alt={svc.name}
                            style={{ width: 60, height: 45, borderRadius: 8, objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{
                            width: 60, height: 45, borderRadius: 8,
                            background: 'linear-gradient(135deg,#5F33E1,#8B5CF6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontWeight: 700, fontSize: 14,
                          }}>
                            {svc.name?.[0] || 'Q'}
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>{svc.name}</div>
                          <Tag color="purple" style={{ fontSize: 10, margin: '4px 0 0', borderRadius: 6 }}>
                            {svc.service_type?.name || 'Xizmat'}
                          </Tag>
                        </div>
                      </div>
                      {svc.statistics?.average_rating > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                          <StarFilled style={{ color: '#faad14', fontSize: 12 }} />
                          <span style={{ fontSize: 12, fontWeight: 600 }}>
                            {Number(svc.statistics.average_rating).toFixed(1)}
                          </span>
                          <span style={{ fontSize: 11, color: '#999' }}>
                            ({svc.statistics.total_comments} baho)
                          </span>
                        </div>
                      )}
                      {svc.location?.formatted_address && (
                        <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>
                          <EnvironmentOutlined /> {svc.location.formatted_address}
                        </div>
                      )}
                      <DateLine serviceId={svc.id} />
                      <Button
                        type="primary"
                        size="small"
                        block
                        onClick={() => navigate(`/services/${svc.id}`)}
                        style={{ borderRadius: 8, background: P, border: 'none', marginTop: 4 }}
                      >
                        Batafsil ko'rish
                      </Button>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>

        {/* Sidebar list */}
        <div style={{
          width: 340, background: 'var(--qk-card-bg)', borderLeft: '1px solid var(--qk-border)',
          overflowY: 'auto', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--qk-border)' }}>
            <Text strong style={{ fontSize: 14 }}>Xizmatlar ro'yxati</Text>
          </div>
          {mapServices.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              <EnvironmentOutlined style={{ fontSize: 32, marginBottom: 8, display: 'block' }} />
              Xizmatlar topilmadi
            </div>
          )}
          {mapServices.map(svc => {
            const isActive = selectedService?.id === svc.id;
            return (
              <div
                key={svc.id}
                onClick={() => {
                  setSelectedService(svc);
                  const lat = Number(svc.lat || svc.location?.lat);
                  const lng = Number(svc.long || svc.lng || svc.location?.lng);
                  if (lat && lng) {
                    setFlyCenter([lat, lng]);
                    setFlyZoom(14);
                  }
                }}
                style={{
                  display: 'flex', gap: 12, padding: '12px 16px',
                  cursor: 'pointer', borderBottom: '1px solid #f5f5f5',
                  background: isActive ? 'rgba(95,51,225,0.04)' : '#fff',
                  borderLeft: isActive ? `3px solid ${P}` : '3px solid transparent',
                  transition: 'all 0.2s',
                }}
              >
                {svc.images?.[0]?.file_path ? (
                  <img
                    src={IMG_BASE + svc.images[0].file_path}
                    alt={svc.name}
                    style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : (
                  <Avatar
                    size={56}
                    style={{
                      borderRadius: 10, flexShrink: 0,
                      background: 'linear-gradient(135deg,#5F33E1,#8B5CF6)',
                      fontSize: 20, fontWeight: 700,
                    }}
                  >
                    {svc.name?.[0]}
                  </Avatar>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3, marginBottom: 2 }}>
                    {svc.name}
                  </div>
                  <Tag
                    color={TYPE_COLORS[svc.vendor_types_id] || P}
                    style={{ fontSize: 10, borderRadius: 6, margin: '0 0 4px' }}
                  >
                    {svc.service_type?.name || 'Xizmat'}
                  </Tag>
                  <div style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {svc.region?.nameUz}{svc.district ? `, ${svc.district.nameUz}` : ''}
                  </div>
                  {svc.statistics?.average_rating > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                      <StarFilled style={{ color: '#faad14', fontSize: 10 }} />
                      <span style={{ fontSize: 11, fontWeight: 600 }}>
                        {Number(svc.statistics.average_rating).toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>
                <Button
                  type="link"
                  size="small"
                  onClick={(e) => { e.stopPropagation(); navigate(`/services/${svc.id}`); }}
                  style={{ color: P, fontSize: 11, padding: 0, alignSelf: 'center' }}
                >
                  Ochish →
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Map legend */}
      <div style={{
        position: 'absolute', bottom: 20, left: 20, zIndex: 1000,
        background: 'rgba(255,255,255,0.95)', borderRadius: 12,
        padding: '10px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
        display: 'flex', gap: 12, flexWrap: 'wrap',
      }}>
        {serviceTypes.map(t => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
            <div style={{
              width: 12, height: 12, borderRadius: '50%',
              background: TYPE_COLORS[t.id] || P,
            }} />
            <span>{t.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
